/**
 * invite-link.ts — pure parsing/validation for the lander's inbound URL (ENG-417).
 *
 * The shared link is `https://<zone>/invite/<code>` where `<code>` is the ENG-416
 * shareable code: `invite_<uuid>` + `.` + `invtok_<64 hex>` (see the mobile app's
 * lib/invite-code.ts — identity-persona mints ids as `invite_${uuid}` and tokens
 * as `invtok_${uuid}${uuid}` with dashes stripped). This module validates the
 * SHAPE only — strictly, against the exact minted grammar — with NO network call:
 * a malformed link renders the generic invalid state immediately, and the server
 * is never consulted for anything but a real, well-formed claim. That keeps the
 * lander useless as an enumeration oracle: shape feedback is computable by anyone
 * from the public format, and every server-side deny is the same uniform 404.
 *
 * PHI safety: a code carries no personal data (two opaque identifiers).
 */

/** The two halves of a well-formed invite code (the accept/claim call's inputs). */
export interface ParsedInviteCode {
  invitationId: string;
  token: string;
}

const INVITATION_ID_RE = /^invite_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const TOKEN_RE = /^invtok_[0-9a-f]{64}$/;

/**
 * Parse a raw code (the path segment after /invite/) into its halves, or null
 * when it does not match the exact minted grammar. Split on the FIRST `.` —
 * neither half can contain one — and validate each half strictly.
 */
export function parseInviteCode(raw: string): ParsedInviteCode | null {
  const trimmed = raw.trim();
  const delimiterIndex = trimmed.indexOf(".");
  if (delimiterIndex <= 0 || delimiterIndex === trimmed.length - 1) return null;
  const invitationId = trimmed.slice(0, delimiterIndex);
  const token = trimmed.slice(delimiterIndex + 1);
  if (!INVITATION_ID_RE.test(invitationId)) return null;
  if (!TOKEN_RE.test(token)) return null;
  return { invitationId, token };
}

/**
 * Extract the code from a lander pathname (`/invite/<code>`, optional trailing
 * slash). Returns null for any other path shape; the caller then renders the
 * generic invalid state. The segment is URI-decoded defensively (a share channel
 * may percent-encode), and a decode failure is treated as malformed.
 */
export function inviteCodeFromPathname(pathname: string): ParsedInviteCode | null {
  const match = /^\/invite\/([^/]+)\/?$/.exec(pathname);
  const rawSegment = match?.[1];
  if (rawSegment === undefined) return null;
  let segment: string;
  try {
    segment = decodeURIComponent(rawSegment);
  } catch {
    return null;
  }
  return parseInviteCode(segment);
}

/** The store the current device should be routed to. Desktop shows both badges. */
export type StorePlatform = "ios" | "android" | "unknown";

/**
 * Classify the visiting device from its user agent, for store-badge emphasis
 * only (both badges stay reachable — detection is a hint, never a gate). iPadOS
 * 13+ masquerades as macOS, so a Mac UA with touch support counts as iOS.
 */
export function detectStorePlatform(userAgent: string, maxTouchPoints: number): StorePlatform {
  if (/android/i.test(userAgent)) return "android";
  if (/iphone|ipad|ipod/i.test(userAgent)) return "ios";
  if (/macintosh/i.test(userAgent) && maxTouchPoints > 1) return "ios";
  return "unknown";
}
